/**
 * Audience targeting for the funnel. A QR card can point at
 * start.bonded.studio/?app=for-couples (mirroring the SEO pages
 * /app/for-couples, /app/for-first-dates, /app/for-friends). One dynamic
 * funnel swaps hero + value-prop + framing per audience; default is
 * "for-anyone". The resolved key is persisted on the funnel session so admin
 * analytics can segment by entry audience.
 */
export const AUDIENCE_KEYS = ['for-couples', 'for-first-dates', 'for-friends', 'for-anyone'] as const;
export type AudienceKey = (typeof AUDIENCE_KEYS)[number];

// Accepts the canonical `for-*` slugs and their bare aliases.
const ALIASES: Record<string, AudienceKey> = {
  'for-couples': 'for-couples',
  couples: 'for-couples',
  'for-first-dates': 'for-first-dates',
  'first-dates': 'for-first-dates',
  dates: 'for-first-dates',
  'for-friends': 'for-friends',
  friends: 'for-friends',
  'for-anyone': 'for-anyone',
  anyone: 'for-anyone',
};

export function resolveAudience(search: string = window.location.search): AudienceKey {
  const raw = new URLSearchParams(search).get('app')?.toLowerCase().trim() ?? '';
  return ALIASES[raw] ?? 'for-anyone';
}

/** Presentational config per audience. Copy itself lives in i18n under audiences.<key>. */
export interface AudiencePresentation {
  /** Card accent behind the hero art. */
  accent: string;
  /** Hero illustration filename in bonded-design-system/assets/illustrations. */
  heroArt: 'hands-holding' | 'sky-friends' | 'impressed' | 'the-world';
}

export const AUDIENCE_PRESENTATION: Record<AudienceKey, AudiencePresentation> = {
  'for-couples': { accent: 'var(--bubblegum)', heroArt: 'hands-holding' },
  'for-first-dates': { accent: 'var(--sunshine)', heroArt: 'impressed' },
  'for-friends': { accent: 'var(--sky)', heroArt: 'sky-friends' },
  'for-anyone': { accent: 'var(--lilac)', heroArt: 'the-world' },
};
